import api from './api';

// Types
export interface PayeeAccount {
  _id: string;
  companyId: string;
  userId?: string;
  name: string;
  phone?: string;
  email?: string;
  bankDetails?: {
    accountName?: string;
    accountNumber?: string;
    ifscCode?: string;
    bankName?: string;
    upiId?: string;
  };
  totalDue: number;
  totalPaid: number;
  expenseCount: number;
  isActive: boolean;
  isUnassigned?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Expense {
  _id: string;
  companyId: string;
  type: 'maintenance' | 'salary' | 'other';
  subtype: 'labour' | 'material' | 'other';
  maintenanceId?: string;
  propertyId?: string | { _id: string; name: string };
  rentalUnitId?: string | { _id: string; unitNumber: string };
  assigneeId?: string | { _id: string; firstName: string; lastName: string };
  payeeAccountId?: string | PayeeAccount;
  payeeSnapshot?: { name: string; phone?: string };
  amount: number;
  amountPaid: number;
  amountDue: number;
  description: string;
  status: 'pending' | 'partial' | 'paid' | 'void';
  notes: Array<{
    authorId: string;
    authorName: string;
    note: string;
    createdAt: string;
  }>;
  attachments?: Array<{
    url: string;
    fileName?: string;
    fileType?: string;
    uploadedAt?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface ExpensePayment {
  _id: string;
  companyId: string;
  payeeAccountId: string | PayeeAccount;
  amount: number;
  paymentMethod: 'cash' | 'bank_transfer' | 'upi' | 'cheque' | 'other';
  paymentDate: string;
  referenceNumber?: string;
  allocations: Array<{
    expenseId: string | Expense;
    amount: number;
    isPartial: boolean;
  }>;
  notes?: string;
  createdAt: string;
}

export interface ExpenseFilters {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  propertyId?: string;
  rentalUnitId?: string;
  assigneeId?: string;
  payeeAccountId?: string;
  type?: string;
  status?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  totalCount: number;
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  totals?: Record<string, number>;
  data: T[];
}

// Expense API
export const getExpenses = async (filters: ExpenseFilters = {}): Promise<PaginatedResponse<Expense>> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  });
  const response = await api.get(`/expenses?${params.toString()}`);
  return response.data;
};

export const getUnassignedExpenses = async (page = 1, limit = 20): Promise<PaginatedResponse<Expense>> => {
  const response = await api.get(`/expenses/unassigned?page=${page}&limit=${limit}`);
  return response.data;
};

export const getExpense = async (id: string): Promise<{ success: boolean; data: Expense }> => {
  const response = await api.get(`/expenses/${id}`);
  return response.data;
};

export const createExpense = async (data: Partial<Expense>): Promise<{ success: boolean; data: Expense }> => {
  const response = await api.post('/expenses', data);
  return response.data;
};

export const updateExpense = async (id: string, data: Partial<Expense>): Promise<{ success: boolean; data: Expense }> => {
  const response = await api.put(`/expenses/${id}`, data);
  return response.data;
};

export const deleteExpense = async (id: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`/expenses/${id}`);
  return response.data;
};

export const assignPayee = async (expenseId: string, payeeAccountId: string | null): Promise<{ success: boolean; data: Expense }> => {
  const response = await api.patch(`/expenses/${expenseId}/assign-payee`, { payeeAccountId });
  return response.data;
};

export const markExpensePaid = async (expenseId: string, amountPaid?: number): Promise<{ success: boolean; data: Expense }> => {
  const response = await api.put(`/expenses/${expenseId}/mark-paid`, { amountPaid });
  return response.data;
};

export const markExpenseVoid = async (expenseId: string): Promise<{ success: boolean; data: Expense }> => {
  const response = await api.put(`/expenses/${expenseId}/mark-void`);
  return response.data;
};

// Payee Account API
export const getPayeeAccounts = async (filters: { page?: number; limit?: number; search?: string; hasBalance?: string } = {}): Promise<PaginatedResponse<PayeeAccount>> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  });
  const response = await api.get(`/payee-accounts?${params.toString()}`);
  return response.data;
};

export const getPayeeAccount = async (id: string): Promise<{ success: boolean; data: PayeeAccount & { recentExpenses: Expense[] } }> => {
  const response = await api.get(`/payee-accounts/${id}`);
  return response.data;
};

export const createPayeeAccount = async (data: Partial<PayeeAccount>): Promise<{ success: boolean; data: PayeeAccount }> => {
  const response = await api.post('/payee-accounts', data);
  return response.data;
};

export const updatePayeeAccount = async (id: string, data: Partial<PayeeAccount>): Promise<{ success: boolean; data: PayeeAccount }> => {
  const response = await api.patch(`/payee-accounts/${id}`, data);
  return response.data;
};

export const deletePayeeAccount = async (id: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`/payee-accounts/${id}`);
  return response.data;
};

export const getPayeeExpenses = async (payeeId: string, page = 1, limit = 20): Promise<PaginatedResponse<Expense>> => {
  const response = await api.get(`/payee-accounts/${payeeId}/expenses?page=${page}&limit=${limit}`);
  return response.data;
};

export const getPayeePayments = async (payeeId: string, page = 1, limit = 20): Promise<PaginatedResponse<ExpensePayment>> => {
  const response = await api.get(`/payee-accounts/${payeeId}/payments?page=${page}&limit=${limit}`);
  return response.data;
};

// Payment API
export const createPayment = async (data: {
  payeeAccountId?: string;
  expenseId?: string;
  amount: number;
  paymentMethod?: string;
  paymentDate?: string;
  referenceNumber?: string;
  notes?: string;
}): Promise<{ success: boolean; data: ExpensePayment }> => {
  const response = await api.post('/expense-payments', data);
  return response.data;
};

export const getPayments = async (filters: { page?: number; limit?: number; payeeAccountId?: string; startDate?: string; endDate?: string; paymentMethod?: string } = {}): Promise<PaginatedResponse<ExpensePayment>> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  });
  const response = await api.get(`/expense-payments?${params.toString()}`);
  return response.data;
};
